// 仓库管理页(admin) — Fluent UI v9 单列列表风格
// 点击行打开仓库详情对话框
// 分页固定在底部(与 浏览/我的仓库 一致):root height:100% + body(flex:1,滚动) + pagination(flexShrink:0)
import { useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Input,
  Badge,
  Title3,
  Text,
  Button,
} from '@fluentui/react-components';
import {
  SearchRegular,
  DatabaseRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/api/admin';
import { HelpPopover } from '@/components/HelpPopover';
import { useAuthStore } from '@/store/auth';
import { SimpleList, type SimpleListItem } from '@/components/SimpleList';
import { RepoDetailDialog } from '@/components/RepoDetailDialog';

const PAGE_SIZE = 20;

function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: tokens.spacingVerticalXL,
  },
  // 列表区 flex:1 独占剩余高度并滚动,分页才贴底
  body: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: tokens.spacingVerticalL,
  },
  pagination: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: '10px 0',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  pageInfo: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  repoIcon: {
    fontSize: '22px',
    color: tokens.colorBrandForeground1,
  },
  repoName: {
    fontWeight: tokens.fontWeightSemibold,
  },
});

export function AdminRepoManagementPage() {
  const styles = useStyles();
  const me = useAuthStore((s) => s.user);
  const [keyword, setKeyword] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  // 仓库详情对话框
  const [detail, setDetail] = useState<{ owner: string; name: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-repos'],
    queryFn: () => adminApi.listRepos(200, 0),
    enabled: !!me?.is_admin,
  });

  const repos = data?.repos ?? [];

  const filtered = useMemo(() => {
    if (!keyword.trim()) return repos;
    const q = keyword.trim().toLowerCase();
    return repos.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.owner.toLowerCase().includes(q) ||
        (r.description ?? '').toLowerCase().includes(q),
    );
  }, [repos, keyword]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // 客户端分页:只把当前页传给 SimpleList(关键字变化会因 filtered 变化自动回到首页)
  const paged = useMemo(
    () => filtered.slice((pageIndex - 1) * PAGE_SIZE, (pageIndex - 1) * PAGE_SIZE + PAGE_SIZE),
    [filtered, pageIndex],
  );

  const items: SimpleListItem[] = useMemo(
    () =>
      paged.map((r) => ({
        key: r.id,
        media: <DatabaseRegular className={styles.repoIcon} />,
        title: (
          <>
            <span className={styles.repoName}>
              {r.owner}/{r.name}
            </span>
            {r.visibility === 'private' ? (
              <Badge appearance="tint" color="subtle">
                私有
              </Badge>
            ) : (
              <Badge appearance="filled" color="success">
                公开
              </Badge>
            )}
          </>
        ),
        description: r.description
          ? `${r.description} · ${formatBytes(r.size_bytes)} · ${new Date(r.created_at).toLocaleDateString('zh-CN')}`
          : `${formatBytes(r.size_bytes)} · 创建于 ${new Date(r.created_at).toLocaleDateString('zh-CN')}`,
        onClick: () => setDetail({ owner: r.owner, name: r.name }),
      })),
    [paged, styles.repoName, styles.repoIcon],
  );

  return (
    <div className={styles.root}>
      <Title3 as="h1">仓库管理</Title3>

      <div className={styles.toolbar}>
        <Input
          placeholder="搜索仓库名、所有者或描述"
          contentBefore={<SearchRegular />}
          value={keyword}
          onChange={(_, d) => {
            setKeyword(d.value);
            setPageIndex(1);
          }}
          style={{ width: 280 }}
        />
        <HelpPopover content="按仓库名、所有者或描述模糊搜索全部仓库,留空显示全部。" />
      </div>

      {/* 列表区(单独滚动,分页贴底) */}
      <div className={styles.body}>
        <SimpleList
          items={items}
          loading={isLoading}
          emptyText={keyword.trim() ? '没有匹配的仓库' : '暂无仓库'}
          pageSize={Number.POSITIVE_INFINITY}
        />
      </div>

      {/* 分页固定底部 */}
      {total > 0 && (
        <div className={styles.pagination}>
          <Button
            appearance="subtle"
            icon={<ChevronLeftRegular />}
            disabled={pageIndex <= 1}
            onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
          />
          <Text className={styles.pageInfo}>
            第 {pageIndex} / {totalPages} 页 · 共 {total} 个
          </Text>
          <Button
            appearance="subtle"
            icon={<ChevronRightRegular />}
            disabled={pageIndex >= totalPages}
            onClick={() => setPageIndex((p) => Math.min(totalPages, p + 1))}
          />
        </div>
      )}

      {/* 仓库详情对话框 */}
      <RepoDetailDialog
        owner={detail?.owner ?? null}
        name={detail?.name ?? null}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}
